<template>
  <v-row>
    <v-col cols="12">
      <div class="bm-page-head">
        <router-link to="/report/budget-monitoring" class="bm-back">
          <v-icon small color="grey darken-1">mdi-chevron-left</v-icon>Budget Monitoring · Job
        </router-link>
        <span class="grey--text">/</span>
        <div class="bm-title">Job {{ jobId }}</div>
        <div class="bm-subtitle">{{ summary.jobName }}</div>
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          :loading="recomputing"
          :disabled="loading"
          @click="confirmRecompute = true"
        >
          <v-icon left>mdi-refresh</v-icon>Hitung ulang job ini
        </v-btn>
      </div>

      <v-skeleton-loader v-if="loading && !detail" type="card, article"></v-skeleton-loader>
      <empty-state
        v-else-if="!detail"
        icon="mdi-file-question-outline"
        title="Job belum pernah dihitung"
        text="Job ini belum ada di hasil perhitungan — mungkin closed di luar tenggang, atau belum ada di PPIC."
      >
        <v-btn color="primary" class="mt-2" @click="confirmRecompute = true"
          >Hitung ulang job ini</v-btn
        >
      </empty-state>

      <template v-else>
        <!-- identitas + verdict -->
        <v-card class="pa-5 mb-4">
          <v-row align="center">
            <v-col cols="12" md="8">
              <v-row dense>
                <v-col cols="6">
                  <div class="bm-tiny">Customer · status</div>
                  <div class="subtitle-1 bm-ell">
                    {{ summary.customer || "—" }} ·
                    <span v-if="summary.closed"
                      >closed {{ formatDate(summary.closingDate) }} · masih dipantau
                      (tenggang)</span
                    >
                    <span v-else>aktif</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">PO CRM terhubung</div>
                  <div class="subtitle-1 bm-ell">
                    <template v-if="shares.length">
                      <router-link
                        v-for="(s, i) in shares"
                        :key="s.crmPoId"
                        :to="'/report/budget-monitoring/po/' + s.crmPoId"
                        >{{ i ? ", " : "" }}{{ s.poNumber || "#" + s.crmPoId }}</router-link
                      >
                    </template>
                    <span v-else class="orange--text text--darken-3"
                      >tanpa PO CRM — tidak masuk budget mana pun</span
                    >
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">Job lain di PO yang sama</div>
                  <div class="subtitle-1 bm-ell">
                    <template v-if="siblings.length">
                      <router-link
                        v-for="(j, i) in siblings"
                        :key="j.jobId"
                        :to="'/report/budget-monitoring/job/' + j.jobId"
                        >{{ i ? ", " : "" }}{{ j.jobId }} · {{ j.jobName || "—" }}</router-link
                      >
                    </template>
                    <span v-else class="grey--text">tidak ada — job tunggal</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="bm-tiny">Metode alokasi</div>
                  <div class="subtitle-1 bm-ell d-flex align-center">
                    {{ methodCopy }}
                    <v-chip
                      v-if="estimated"
                      x-small
                      color="orange lighten-5"
                      class="orange--text text--darken-3 ml-2"
                      >estimasi</v-chip
                    >
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="4">
              <div
                class="bm-verdict"
                :style="{
                  borderColor: bandHex(summary.bandInfo),
                  background: bandTint(summary.bandInfo),
                }"
              >
                <span class="bm-dot-lg" :style="{ background: bandHex(summary.bandInfo) }"></span>
                <div style="min-width: 0">
                  <div class="bm-cap" :style="{ color: bandHex(summary.bandInfo) }">
                    {{ verdictTitle }}
                  </div>
                  <div class="bm-verdict-value">
                    {{ formatPct(summary.ratioPercent)
                    }}<span class="bm-tiny ml-2">dari bagian budget job</span>
                  </div>
                  <div class="bm-tiny">
                    <template v-if="projected">
                      Proyeksi akhir
                      <strong :style="{ color: bandHex(summary.projectedBand) }">{{
                        formatPct(summary.projectedRatioPercent)
                      }}</strong>
                      ({{ bandName(summary.projectedBand).toLowerCase() }}) ·
                    </template>
                    Band job informasi saja; email dikirim per PO.
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-row class="mb-1">
          <v-col cols="12" md="4">
            <v-card class="pa-4 bm-kpi bm-kpi--budget fill-height">
              <div class="bm-cap">BAGIAN BUDGET JOB INI</div>
              <div class="bm-kpi-value mt-1">
                <template v-if="shares.length">Rp {{ formatIdr(summary.budgetIdr) }}</template>
                <span v-else class="grey--text">—</span>
              </div>
              <div class="bm-tiny mt-1">
                <template v-if="shares.length === 1">
                  = {{ sharePct(shares[0]) }} × budget
                  {{ shares[0].poNumber || "#" + shares[0].crmPoId }}
                  <span v-if="poBudgetOf(shares[0]) !== null"
                    >(Rp {{ formatIdr(poBudgetOf(shares[0])) }})</span
                  ><br />
                  {{ shareExplain }}
                </template>
                <template v-else-if="shares.length > 1"
                  >Σ bagian dari {{ shares.length }} PO — rincian di tabel Pembagian
                  budget.</template
                >
                <template v-else
                  >Hubungkan job ke PO customer di PPIC agar belanjanya masuk budget.</template
                >
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pa-4 bm-kpi bm-kpi--usage fill-height">
              <div class="bm-cap">PEMAKAIAN JOB INI</div>
              <div class="bm-kpi-value mt-1">Rp {{ formatIdr(summary.usageIdr) }}</div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span
                  ><span class="bm-dot" style="background: #1976d2"></span>Belanja PO supplier</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.spendIdr) }}
                  <v-chip
                    x-small
                    :color="Number(summary.spendIdr) ? 'blue lighten-5' : 'grey lighten-3'"
                    :class="
                      Number(summary.spendIdr)
                        ? 'blue--text text--darken-2 ml-2 bm-chip-link'
                        : 'grey--text ml-2'
                    "
                    @click="Number(summary.spendIdr) && openBreakdown('SPEND')"
                    >PO supplier</v-chip
                  >
                </span>
              </div>
              <div class="bm-tiny mt-1 d-flex justify-space-between align-center">
                <span
                  ><span class="bm-dot" style="background: #3949ab"></span>Pemakaian stok (MR)</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.stockIdr) }}
                  <v-chip
                    x-small
                    :color="Number(summary.stockIdr) ? 'indigo lighten-5' : 'grey lighten-3'"
                    :class="
                      Number(summary.stockIdr)
                        ? 'indigo--text ml-2 bm-chip-link'
                        : 'grey--text ml-2'
                    "
                    @click="Number(summary.stockIdr) && openBreakdown('STOCK')"
                    >MR</v-chip
                  >
                </span>
              </div>
              <div
                v-if="Number(summary.committedIdr) > 0"
                class="bm-tiny mt-1 d-flex justify-space-between align-center"
              >
                <span
                  ><span class="bm-dot" style="background: #90a4ae"></span>Committed (tidak masuk
                  rasio)</span
                >
                <span class="bm-num d-flex align-center">
                  {{ formatIdr(summary.committedIdr) }}
                  <v-chip
                    x-small
                    color="blue-grey lighten-5"
                    class="blue-grey--text text--darken-2 ml-2 bm-chip-link"
                    @click="openBreakdown('COMMITTED')"
                    >rincian</v-chip
                  >
                </span>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card
              v-if="projected"
              class="pa-4 bm-kpi fill-height"
              :class="estimated ? 'bm-kpi--estimate' : ''"
              :style="estimated ? '' : { borderTopColor: bandHex(summary.projectedBand) }"
            >
              <div class="bm-cap">PROYEKSI AKHIR · KEYAKINAN</div>
              <div class="bm-kpi-value mt-1 d-flex align-center">
                {{ formatPct(summary.projectedRatioPercent) }}
                <v-chip x-small dark :color="bandColor(summary.projectedBand)" class="ml-2">{{
                  bandName(summary.projectedBand)
                }}</v-chip>
              </div>
              <div class="bm-tiny mt-1">
                = (pemakaian {{ formatIdr(summary.usageIdr) }} + committed
                {{ formatIdr(summary.committedIdr) }} + sisa BOM
                <a v-if="Number(summary.bomRemainingIdr)" @click="openBreakdown('BOM')">{{
                  formatIdr(summary.bomRemainingIdr)
                }}</a
                ><template v-else>0</template>) ÷ bagian budget.
                <span v-if="summary.bomNoPriceCount"
                  >{{ summary.bomNoPriceCount }} item tanpa harga tidak dihitung.</span
                >
                Keyakinan:
                <template v-if="estimated">
                  <strong class="orange--text text--darken-3">estimasi</strong> — budget dibagi
                  proporsional; isi <em>panel code</em> di PPIC atau cocokkan part number item PO.
                </template>
                <template v-else
                  ><strong>pasti</strong> — tidak ada pembagian proporsional.</template
                >
              </div>
            </v-card>
            <v-card
              v-else
              class="pa-4 bm-kpi fill-height"
              :class="estimated ? 'bm-kpi--estimate' : ''"
            >
              <div class="bm-cap">KEYAKINAN ANGKA</div>
              <div
                class="bm-kpi-value mt-1"
                :class="estimated ? 'orange--text text--darken-3' : ''"
              >
                {{ estimated ? "Estimasi" : "Pasti" }}
              </div>
              <div class="bm-tiny mt-1">
                <template v-if="estimated">
                  Budget dibagi proporsional karena job berbagi PO tanpa item yang cocok. Agar
                  pasti: isi <em>panel code</em> di PPIC atau cocokkan part number item PO.
                </template>
                <template v-else-if="shares.length"
                  >Job ini satu-satunya job PO tersebut, atau item PO cocok dengan job — tidak ada
                  pembagian proporsional.</template
                >
                <template v-else>Tanpa PO CRM tidak ada budget yang bisa dibandingkan.</template>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row align="start">
          <v-col cols="12" md="7">
            <v-card>
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Sumber pemakaian</div>
                  <div class="bm-sec-sub">
                    PO supplier, committed, dan MR job ini. Buka rinciannya per jenis.
                  </div>
                </div>
              </div>
              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td style="width: 140px">
                      <v-chip x-small color="blue lighten-5" class="blue--text text--darken-2"
                        >PO supplier</v-chip
                      >
                    </td>
                    <td>Belanja PO supplier yang approve, dialokasikan ke PO CRM job ini.</td>
                    <td class="text-right bm-num" style="width: 150px">
                      {{ formatIdr(summary.spendIdr) }}
                    </td>
                    <td class="text-right" style="width: 90px">
                      <a v-if="Number(summary.spendIdr)" @click="openBreakdown('SPEND')">rincian</a>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip
                        x-small
                        color="blue-grey lighten-5"
                        class="blue-grey--text text--darken-2"
                        >Committed</v-chip
                      >
                    </td>
                    <td>PR tanpa PO dan PO belum approve — belum masuk rasio.</td>
                    <td class="text-right bm-num grey--text text--darken-1">
                      {{ formatIdr(summary.committedIdr) }}
                    </td>
                    <td class="text-right">
                      <a v-if="Number(summary.committedIdr)" @click="openBreakdown('COMMITTED')"
                        >rincian</a
                      >
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <v-chip x-small color="indigo lighten-5" class="indigo--text"
                        >MR / stok</v-chip
                      >
                    </td>
                    <td>Material Request yang di-pick, neto setelah dedup dengan PO job.</td>
                    <td class="text-right bm-num">{{ formatIdr(summary.stockIdr) }}</td>
                    <td class="text-right">
                      <a v-if="Number(summary.stockIdr)" @click="openBreakdown('STOCK')">rincian</a>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-if="!Number(summary.stockIdr)" class="bm-empty-inline">
                <v-icon size="36" color="grey lighten-1">mdi-package-variant-closed</v-icon>
                <div>Belum ada Material Request untuk job ini.</div>
                <div class="bm-tiny">
                  Pemakaian stok muncul setelah MR di-pick di PPIC dan dijurnal di accounting.
                </div>
              </div>
            </v-card>

            <!-- produk job (panel code) -->
            <v-card class="mt-4">
              <div class="bm-sec">
                <div style="min-width: 0">
                  <div class="bm-sec-title">Produk job (panel code)</div>
                  <div class="bm-sec-sub">
                    Panel code = produk yang dikerjakan job ini. Tautan ke item PO customer
                    menentukan bagian budget; belanja &amp; stok = yang dialokasikan lewat panel
                    code itu.
                  </div>
                </div>
                <v-chip
                  v-if="unlinkedPanelCount"
                  x-small
                  color="orange lighten-5"
                  class="orange--text text--darken-3"
                  >{{ unlinkedPanelCount }} dari {{ panelCodes.length }} belum terhubung</v-chip
                >
                <v-chip
                  v-else-if="panelCodes.length"
                  x-small
                  color="green lighten-5"
                  class="green--text text--darken-3"
                  >Semua terhubung</v-chip
                >
              </div>
              <v-simple-table dense class="bm-table bm-fixed">
                <thead>
                  <tr>
                    <th style="width: 100px">Panel code</th>
                    <th>Item PO customer</th>
                    <th style="width: 124px">Item gudang</th>
                    <th class="text-right" style="width: 94px">Belanja PO</th>
                    <th class="text-right" style="width: 80px">Stok</th>
                    <th class="text-right" style="width: 108px">Sisa BOM</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in panelCodes" :key="p.id">
                    <td>
                      <v-chip
                        x-small
                        :color="p.poProductId ? 'green lighten-5' : 'grey lighten-3'"
                        class="bm-chip-ell"
                        :class="p.poProductId ? 'green--text text--darken-3' : ''"
                        :title="(p.name || '#' + p.id) + ' · panel code #' + p.id"
                        >{{ p.name || "#" + p.id }}</v-chip
                      >
                    </td>
                    <td>
                      <div v-if="p.poProductId" class="bm-cell">
                        <router-link
                          v-if="p.crmPoId"
                          :to="'/report/budget-monitoring/po/' + p.crmPoId"
                          class="text-decoration-none"
                          :title="poItemTitle(p)"
                          >{{ p.poNumber || "#" + p.crmPoId }} · {{ poItemLabel(p) }}</router-link
                        >
                        <span v-else :title="'item PO #' + p.poProductId"
                          >item PO #{{ p.poProductId }} (PO di luar cakupan)</span
                        >
                        <span class="bm-tiny" :title="p.poPartName">{{ poItemSub(p) }}</span>
                      </div>
                      <div v-else class="bm-cell">
                        <v-chip x-small color="grey lighten-3" class="align-self-start"
                          >belum terhubung</v-chip
                        >
                        <span class="bm-tiny"
                          >isi item PO di panel code PPIC agar bagian budget pasti</span
                        >
                      </div>
                    </td>
                    <td>
                      <div class="bm-cell">
                        <span :title="p.partNumber">{{
                          p.partNumber || (p.itemId ? "#" + p.itemId : "—")
                        }}</span>
                        <span class="bm-tiny" :title="p.partName">{{
                          p.partName ||
                            (p.itemId ? "nama tidak ada di item master" : "tanpa item gudang")
                        }}</span>
                      </div>
                    </td>
                    <td class="text-right bm-num">{{ formatIdr(p.spendIdr) }}</td>
                    <td class="text-right bm-num">{{ formatIdr(p.stockIdr) }}</td>
                    <td class="text-right">
                      <div v-if="p.bomItemCount" class="bm-cell align-end">
                        <span class="bm-num">{{ formatIdr(p.bomRemainingIdr) }}</span>
                        <v-chip
                          x-small
                          color="pink lighten-5"
                          class="pink--text text--darken-4 bm-chip-link"
                          :title="
                            'Sisa BOM ' + (p.name || '#' + p.id) + ': ' + p.bomItemCount + ' item'
                          "
                          @click="openBreakdown('BOM', p)"
                          >{{ p.bomItemCount }} item</v-chip
                        >
                      </div>
                      <div v-else class="bm-cell align-end">
                        <span class="grey--text">—</span>
                        <span class="bm-tiny">{{ projected ? "tanpa sisa BOM" : "" }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!panelCodes.length">
                    <td colspan="6" class="text-center grey--text py-6">
                      Job ini belum punya panel code di PPIC.
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div class="bm-tiny d-flex align-center flex-wrap px-5 py-3" style="gap: 8px 20px">
                <span>
                  Tanpa panel code (dialokasikan proporsional / item cocok): belanja Rp
                  {{ formatIdr(detail.unassignedSpendIdr) }} · stok Rp
                  {{ formatIdr(detail.unassignedStockIdr) }}
                </span>
                <span v-if="projected">
                  Sisa BOM job:
                  <strong>Rp {{ formatIdr(detail.bomRemainingIdr) }}</strong> ·
                  {{ detail.bomItemCount || 0 }} item<template v-if="detail.bomNoPriceCount">
                    · {{ detail.bomNoPriceCount }} tanpa harga</template
                  >
                </span>
                <v-spacer></v-spacer>
                <a
                  :href="ppicShortageUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-decoration-none"
                >
                  Lihat shortage BOM di PPIC (MPS Dashboard)
                  <v-icon x-small color="primary">mdi-open-in-new</v-icon>
                </a>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card v-for="po in poDetails" :key="po.crmPoId" class="mb-4">
              <div class="bm-sec">
                <div>
                  <div class="bm-sec-title">Pembagian budget {{ po.poNumber }}</div>
                  <div class="bm-sec-sub">Bagaimana budget PO dibagi ke setiap job.</div>
                </div>
              </div>
              <v-simple-table dense class="bm-fixed">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th class="text-right" style="width: 70px">Share</th>
                    <th class="text-right" style="width: 130px">Bagian budget</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="j in po.jobs"
                    :key="j.jobId"
                    :class="{ 'blue lighten-5': j.jobId === jobId }"
                  >
                    <td class="bm-ell">
                      <router-link
                        v-if="j.jobId !== jobId"
                        :to="'/report/budget-monitoring/job/' + j.jobId"
                        class="text-decoration-none"
                        >{{ j.jobId }} · {{ j.jobName || "—" }}</router-link
                      >
                      <strong v-else>{{ j.jobId }} · {{ j.jobName || "—" }} (job ini)</strong>
                      <span class="bm-tiny ml-1">{{ methodLabel(j.method) }}</span>
                    </td>
                    <td class="text-right bm-num">
                      {{
                        j.share !== null && j.share !== undefined
                          ? Math.round(Number(j.share) * 100) + " %"
                          : "—"
                      }}
                    </td>
                    <td class="text-right bm-num">{{ formatIdr(j.budgetIdr) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Budget PO</td>
                    <td class="text-right bm-num">100 %</td>
                    <td class="text-right bm-num font-weight-medium">
                      {{ formatIdr(po.budgetIdr) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
            <v-card v-if="!poDetails.length && shares.length" class="pa-4 bm-tiny"
              >Memuat pembagian budget…</v-card
            >
          </v-col>
        </v-row>
      </template>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>
    <budget-recompute-dialog
      v-model="confirmRecompute"
      scope="job"
      :subject="'job ' + jobId"
      :last-run-at="formatDateTime(summary.computedAt)"
      :loading="recomputing"
      @confirm="recompute"
    ></budget-recompute-dialog>
  </v-row>
</template>

<script>
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import BudgetRecomputeDialog from "@/components/budget/BudgetRecomputeDialog.vue";
import EmptyState from "@/components/budget/EmptyState.vue";
import {
  bandColor,
  bandHex,
  bandName,
  bandTint,
  formatDate,
  formatDateTime,
  formatIdr,
  formatPct,
  hasProjection,
  methodLabel,
  shareOf,
} from "@/utils/budgetMonitor";
import { PPIC_UI } from "@/services/config";
import "@/components/budget/budget-monitor.css";

export default {
  name: "budget-monitoring-job-detail",
  components: { BudgetSpendBreakdown, BudgetRecomputeDialog, EmptyState },
  data: () => ({
    loading: false,
    recomputing: false,
    confirmRecompute: false,
    detail: null,
    /** PO detail per linked PO: { crmPoId, poNumber, budgetIdr, jobs: JobShareDto[] } */
    poDetails: [],
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
  }),
  computed: {
    jobId() {
      return Number(this.$route.params.jobId);
    },
    summary() {
      return (this.detail && this.detail.summary) || {};
    },
    shares() {
      return (this.detail && this.detail.budgetShares) || [];
    },
    panelCodes() {
      return (this.detail && this.detail.panelCodes) || [];
    },
    unlinkedPanelCount() {
      return this.panelCodes.filter(p => !p.poProductId).length;
    },
    projected() {
      return hasProjection(this.summary);
    },
    ppicShortageUrl() {
      const base = PPIC_UI[process.env.VUE_APP_API_ENV] || PPIC_UI.default;
      return base + "/#/mpsdashboard";
    },
    mine() {
      // this job's row inside each PO's job list (share + method)
      return this.poDetails
        .map(po => (po.jobs || []).find(j => j.jobId === this.jobId))
        .filter(x => x);
    },
    siblings() {
      const out = [];
      this.poDetails.forEach(po =>
        (po.jobs || []).forEach(j => j.jobId !== this.jobId && out.push(j))
      );
      return out;
    },
    estimated() {
      return this.mine.some(j => j.method === "EVEN_SPLIT" && Number(j.share) < 1);
    },
    methodCopy() {
      if (!this.shares.length) return "—";
      const m = this.mine[0];
      if (!m) return "…";
      const pct =
        m.share !== null && m.share !== undefined ? Math.round(Number(m.share) * 100) + " %" : "";
      return pct ? `${methodLabel(m.method)} · ${pct}` : methodLabel(m.method);
    },
    shareExplain() {
      const m = this.mine[0];
      if (!m) return "";
      if (Number(m.share) >= 1) return "Job tunggal untuk PO ini — seluruh budget PO.";
      return m.method === "PROPORTIONAL"
        ? `Dibagi rata ${(this.poDetails[0] && this.poDetails[0].jobs.length) ||
            2} job karena item PO tidak cocok dengan job.`
        : `Dibagi menurut ${methodLabel(m.method).toLowerCase()}.`;
    },
    verdictTitle() {
      const b = this.summary.bandInfo;
      return b === "RED"
        ? "MERAH · MELEBIHI BUDGET"
        : b === "YELLOW"
        ? "KUNING · MENDEKATI BATAS"
        : b === "GREEN"
        ? "HIJAU · DALAM BUDGET"
        : "TANPA BUDGET";
    },
  },
  watch: {
    "$route.params.jobId"() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatIdr,
    formatPct,
    formatDate,
    formatDateTime,
    bandColor,
    bandHex,
    bandName,
    bandTint,
    methodLabel,
    /** part number when CRM has one, else the item name, else the PO item id */
    poItemLabel(p) {
      return p.poPartNumber || p.poPartName || "item #" + p.poProductId;
    },
    poItemTitle(p) {
      return (p.poNumber || "#" + p.crmPoId) + " · " + this.poItemLabel(p);
    },
    /** second line: the name when the first line shows a part number, and the item value when CRM has one */
    poItemSub(p) {
      const parts = [];
      if (p.poPartNumber && p.poPartName) parts.push(p.poPartName);
      if (Number(p.poItemValue) > 0) parts.push("Rp " + formatIdr(p.poItemValue));
      else parts.push("nilai item 0 di CRM");
      return parts.join(" · ");
    },
    sharePct(s) {
      const v = shareOf(s, this.poBudgetOf(s));
      return v === null ? "—" : Math.round(v * 100) + " %";
    },
    poBudgetOf(s) {
      const po = this.poDetails.find(p => p.crmPoId === s.crmPoId);
      return po ? po.budgetIdr : null;
    },
    async load() {
      this.loading = true;
      this.poDetails = [];
      try {
        this.detail = await this.$store.dispatch("budgetMonitor/fetchJobDetail", this.jobId);
        await this.loadPoDetails();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.detail = null;
        if (status !== 404)
          this.$store.dispatch("toast", { type: "error", message: "Gagal memuat detail job" });
      } finally {
        this.loading = false;
      }
    },
    async loadPoDetails() {
      const out = [];
      for (const s of this.shares) {
        try {
          const d = await this.$store.dispatch("budgetMonitor/fetchPoDetail", s.crmPoId);
          out.push({
            crmPoId: s.crmPoId,
            poNumber: (d.summary && d.summary.poNumber) || s.poNumber || "#" + s.crmPoId,
            budgetIdr: d.summary ? d.summary.budgetIdr : null,
            jobs: d.jobs || [],
          });
        } catch (e) {
          out.push({
            crmPoId: s.crmPoId,
            poNumber: s.poNumber || "#" + s.crmPoId,
            budgetIdr: null,
            jobs: [],
          });
        }
      }
      this.poDetails = out;
    },
    openBreakdown(bucket, panel) {
      this.breakdown = {
        jobId: this.jobId,
        jobName: this.summary.jobName,
        bucket,
        panelCodeId: panel ? panel.id : undefined,
        panelCodeName: panel ? panel.name : undefined,
        expectedAmount:
          bucket === "STOCK"
            ? this.summary.stockIdr
            : bucket === "COMMITTED"
            ? this.summary.committedIdr
            : bucket === "BOM"
            ? panel
              ? panel.bomRemainingIdr
              : this.summary.bomRemainingIdr
            : this.summary.spendIdr,
      };
      this.showBreakdown = true;
    },
    async recompute() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputeJob", this.jobId);
        this.confirmRecompute = false;
        this.$store.dispatch("toast", {
          type: res && res.status === "DONE" ? "success" : "warning",
          message:
            res && res.status === "DONE"
              ? "Job dihitung ulang."
              : `Perhitungan ${res && res.status} (${(res && res.errors && res.errors.length) ||
                  0} catatan)`,
        });
        await this.load();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.confirmRecompute = false;
        this.$store.dispatch("toast", {
          type: status === 409 ? "warning" : "error",
          message:
            status === 409
              ? "Perhitungan lain sedang berjalan — tunggu selesai."
              : "Gagal menghitung ulang",
        });
      } finally {
        this.recomputing = false;
      }
    },
  },
};
</script>

<style scoped>
.bm-back {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
  white-space: nowrap;
}
.bm-verdict {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 4px;
  border: thin solid;
  min-width: 0;
}
.bm-verdict-value {
  font-size: 22px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bm-dot,
.bm-dot-lg {
  display: inline-block;
  border-radius: 50%;
  flex: none;
}
.bm-dot {
  width: 8px;
  height: 8px;
  margin-right: 6px;
  vertical-align: middle;
}
.bm-dot-lg {
  width: 12px;
  height: 12px;
}
.bm-empty-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  padding: 28px 20px;
  color: rgba(0, 0, 0, 0.6);
  border-top: thin solid rgba(0, 0, 0, 0.12);
}
</style>
